import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { Account, LoginCredentials, User } from '../models'

import * as authService from '../services/auth'
import * as userService from '../services/user'

const TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const SELECTED_ACCOUNT_KEY = 'selected_account'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const refreshToken = ref<string | null>(localStorage.getItem(REFRESH_TOKEN_KEY))
  const selectedAccount = ref<Account | null>(null)
  const isLoading = ref(false)

  // Carregar conta selecionada do localStorage
  const loadSelectedAccount = () => {
    const stored = localStorage.getItem(SELECTED_ACCOUNT_KEY)
    if (stored) {
      try {
        selectedAccount.value = JSON.parse(stored)
      } catch {
        selectedAccount.value = null
      }
    }
  }

  // Persistir conta selecionada no localStorage
  watch(selectedAccount, (newAccount) => {
    if (newAccount) {
      localStorage.setItem(SELECTED_ACCOUNT_KEY, JSON.stringify(newAccount))
    } else {
      localStorage.removeItem(SELECTED_ACCOUNT_KEY)
    }
  })

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)

  const userInitials = computed(() => {
    if (!user.value) return ''

    const firstInitial = user.value.first_name.charAt(0).toUpperCase()
    const lastInitial = user.value.last_name.charAt(0).toUpperCase()

    return `${firstInitial}${lastInitial}`
  })

  const userFullName = computed(() => {
    if (!user.value) return ''
    return `${user.value.first_name} ${user.value.last_name}`
  })

  const setTokens = (access: string, refresh: string) => {
    accessToken.value = access
    refreshToken.value = refresh
    localStorage.setItem(TOKEN_KEY, access)
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
  }

  const clearTokens = () => {
    accessToken.value = null
    refreshToken.value = null
    selectedAccount.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(SELECTED_ACCOUNT_KEY)
  }

  const login = async (credentials: LoginCredentials) => {
    try {
      isLoading.value = true

      const tokens = await authService.login(credentials)
      setTokens(tokens.access, tokens.refresh)

      const userData = await userService.fetchCurrentUser()
      user.value = userData

      // Seleciona a primeira conta automaticamente se houver
      if (userData.accounts && userData.accounts.length > 0 && !selectedAccount.value) {
        selectedAccount.value = userData.accounts[0]
      }

      return { success: true }
    } catch (error: any) {
      clearTokens()

      return {
        success: false,
        error: error.message || 'Erro ao fazer login. Tente novamente.',
      }
    } finally {
      isLoading.value = false
    }
  }

  const setSelectedAccount = (account: Account) => {
    selectedAccount.value = account
  }

  const logout = () => {
    user.value = null
    clearTokens()
  }

  const fetchUser = async () => {
    if (!accessToken.value) return

    try {
      const userData = await userService.fetchCurrentUser()
      user.value = userData
    } catch {
      clearTokens()
      user.value = null
    }
  }

  const checkAuth = async () => {
    if (!accessToken.value) return false

    try {
      await authService.verifyToken(accessToken.value)
      if (!user.value) {
        await fetchUser()
      }
      return true
    } catch {
      clearTokens()
      user.value = null
      return false
    }
  }

  // Carregar conta ao inicializar
  loadSelectedAccount()

  return {
    user,
    accessToken,
    refreshToken,
    selectedAccount,
    isLoading,
    isAuthenticated,
    userInitials,
    userFullName,
    login,
    logout,
    setSelectedAccount,
    fetchUser,
    checkAuth,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
